/**
 * PDF "Bon de commande client" (CMD-XXXXXX) — Phase 86d.
 *
 * Cohérent visuellement avec le BL (DeliveryNoteDetail.jsx) : même bandeau
 * Maison Thaï, même grille de couleurs, même police Sayonara pour la marque,
 * mais distinct par le titre "BON DE COMMANDE" et la mise en avant des
 * conditions tarifaires HT/TVA/TTC.
 *
 * Pas de DLC ni signature de réception : c'est une commande, pas une livraison.
 * Les totaux HT, TVA 2.6% (denrées suisses) et TTC sont calculés à partir
 * des `lines` côté front (pas de surprise sur le PDF par rapport à l'UI).
 */
import React from "react";
import { Document, Page, View, Text, Image, StyleSheet } from "@react-pdf/renderer";
import { fmtDate, fmtDateTime } from "../lib/format";
import { COMPANY_HEADER } from "../lib/company";

const addressLines = (addr) => {
  if (!addr) return [];
  return String(addr)
    .split(/\n|,\s/)
    .map((s) => s.trim())
    .filter(Boolean);
};

const fmtCHF = (n) =>
  Number(n || 0).toLocaleString("fr-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const styles = StyleSheet.create({
  page: { padding: 36, fontFamily: "Helvetica", fontSize: 9, color: "#1A1C1A" },
  headerRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 },
  companyBlock: { flexDirection: "row", alignItems: "flex-start", gap: 12 },
  companyLogo: { width: 56, height: 56 },
  companyInfo: { flexDirection: "column" },
  companyName: { fontSize: 18, fontFamily: "Helvetica-Bold", marginBottom: 2, letterSpacing: 0.8 },
  companyMeta: { fontSize: 8, color: "#575A57", lineHeight: 1.4 },
  docBlock: { flexDirection: "column", alignItems: "flex-end" },
  docTitle: { fontSize: 18, fontFamily: "Helvetica-Bold", color: "#1F4E3D" },
  docRef: { fontSize: 11, marginTop: 4, color: "#575A57", fontFamily: "Helvetica-Bold" },
  docMeta: { fontSize: 9, marginTop: 2, color: "#575A57" },
  parties: { flexDirection: "row", gap: 16, marginBottom: 18 },
  party: { flex: 1, padding: 12, backgroundColor: "#FAFAF7", borderRadius: 4 },
  partyLabel: { fontSize: 8, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 },
  partyName: { fontSize: 11, fontFamily: "Helvetica-Bold", marginBottom: 2 },
  partyText: { fontSize: 9, color: "#575A57", marginBottom: 1 },

  // Phase 87u — Bloc adresse de livraison MISE EN VALEUR (colis web)
  shipBlock: {
    marginBottom: 18,
    padding: 14,
    backgroundColor: "#FBF4E9",
    borderLeft: "4pt solid #8C6B2F",
    borderRadius: 4,
  },
  shipKicker: {
    fontSize: 8,
    color: "#8C6B2F",
    textTransform: "uppercase",
    letterSpacing: 1.2,
    fontFamily: "Helvetica-Bold",
    marginBottom: 6,
  },
  shipName: { fontSize: 16, fontFamily: "Helvetica-Bold", color: "#1A1C1A", marginBottom: 6 },
  shipAddressLine: { fontSize: 13, fontFamily: "Helvetica-Bold", color: "#1A1C1A", lineHeight: 1.35 },
  shipMeta: { fontSize: 9, color: "#575A57", marginTop: 6 },
  shipModePickup: {
    marginTop: 6,
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: "#1F4E3D",
  },
  shipDateBadge: {
    marginTop: 8,
    padding: 6,
    backgroundColor: "#FFF8E1",
    borderLeft: "3pt solid #C9971E",
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: "#5C4A1E",
  },

  table: { borderTop: "1pt solid #E5E5E0", borderLeft: "1pt solid #E5E5E0", borderRight: "1pt solid #E5E5E0", marginBottom: 12 },
  th: { flexDirection: "row", backgroundColor: "#F4F4F0", padding: 6, borderBottom: "1pt solid #E5E5E0" },
  thCell: { fontFamily: "Helvetica-Bold", fontSize: 8, color: "#575A57", textTransform: "uppercase", letterSpacing: 0.5 },
  tr: { flexDirection: "row", padding: 6, borderBottom: "1pt solid #E5E5E0" },
  trAlt: { backgroundColor: "#FBFAF6" },
  td: { fontSize: 9 },
  totalsBox: { marginTop: 4, marginLeft: "auto", width: 220, padding: 10, backgroundColor: "#FAFAF7", borderRadius: 4 },
  totalsRow: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 2 },
  totalsLabel: { fontSize: 9, color: "#575A57" },
  totalsValue: { fontSize: 9, fontFamily: "Helvetica-Bold" },
  totalsFinalRow: { flexDirection: "row", justifyContent: "space-between", marginTop: 6, paddingTop: 6, borderTop: "1pt solid #1A1C1A" },
  totalsFinalLabel: { fontSize: 11, fontFamily: "Helvetica-Bold", color: "#1F4E3D" },
  totalsFinalValue: { fontSize: 11, fontFamily: "Helvetica-Bold", color: "#1F4E3D" },
  notes: { marginTop: 14, padding: 10, backgroundColor: "#FAFAF7", borderLeft: "3pt solid #1F4E3D" },
  notesLabel: { fontSize: 8, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 3 },
  conditionsBox: { marginTop: 14, padding: 10, borderTop: "0.5pt solid #E5E5E0", borderBottom: "0.5pt solid #E5E5E0" },
  conditionsTitle: { fontSize: 8, fontFamily: "Helvetica-Bold", color: "#575A57", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 },
  conditionsText: { fontSize: 8, color: "#575A57", lineHeight: 1.4 },
  signRow: { flexDirection: "row", marginTop: 28, gap: 16 },
  signBox: { flex: 1, padding: 12, borderTop: "1pt solid #1A1C1A" },
  signLabel: { fontSize: 8, color: "#575A57", marginBottom: 2 },
  signValue: { fontSize: 9, fontFamily: "Helvetica-Bold" },
  signEmpty: { fontSize: 9, color: "#A3A6A3", fontFamily: "Helvetica-Oblique" },
  footer: { position: "absolute", bottom: 24, left: 36, right: 36, fontSize: 7, color: "#A3A6A3", textAlign: "center", borderTop: "0.5pt solid #E5E5E0", paddingTop: 6 },
});

const STATUS_LABELS = {
  NOUVELLE: "Projet",
  CONFIRMEE: "Confirmée",
  EN_FABRICATION: "En fabrication",
  PARTIELLEMENT_LIVREE: "Partiellement livrée",
  LIVREE: "Livrée",
  FACTUREE: "Facturée",
  PAYEE: "Payée",
  ANNULEE: "Annulée",
};

export const ClientOrderPdfDocument = ({ co, settings }) => {
  const lines = co.lines || [];

  // Phase 87u — Détection commande boutique web (B2C ou B2B revendeur via web)
  // pour afficher un bloc adresse de livraison MIS EN VALEUR destiné au préparateur
  // de colis (au lieu de fouiller dans l'ERP).
  const isWebOrder = co.distributor_id === "shop-online-virtual";
  const cleanCustomerName = (co.distributor_name || "").replace(/\s*\(web\)\s*$/i, "").trim();
  const shipAddressLines = addressLines(co.distributor_address);
  // Adresse présente = on l'affiche en grand. Sinon, mode pickup explicite = "Retrait".
  // Pour les vieilles commandes sans delivery_mode mais avec adresse, on traite comme delivery.
  const hasShippingAddress = isWebOrder && shipAddressLines.length > 0;
  const isPickup = isWebOrder && !hasShippingAddress && (co.delivery_mode === "pickup");
  const SLOT_LABELS = { matin: "matin (8h-11h)", midi: "midi (11h-14h)", soir: "soir (17h-20h)", autre: "à préciser" };
  const desiredDateLabel = co.desired_delivery_date
    ? `${fmtDate(co.desired_delivery_date)}${co.desired_delivery_slot ? ` · ${SLOT_LABELS[co.desired_delivery_slot] || co.desired_delivery_slot}` : ""}`
    : "";

  // Totaux : on suppose unit_price HT (cohérent avec le reste de l'app).
  // TVA 2.6% = taux réduit suisse pour les denrées alimentaires (Maison Thaï).
  const totalHT = lines.reduce(
    (acc, ln) => acc + (Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0),
    0,
  );
  // TVA par ligne (peut varier — chaque ligne porte son taux dans `vat_rate`)
  const totalVAT = lines.reduce((acc, ln) => {
    const lineHT = (Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0);
    const rate = Number(ln.vat_rate ?? 2.6) / 100;
    return acc + lineHT * rate;
  }, 0);
  const totalTTC = totalHT + totalVAT;

  // Détection mono-taux pour l'affichage propre ("TVA 2.6%" vs "TVA")
  const uniqueRates = Array.from(new Set(lines.map((ln) => Number(ln.vat_rate ?? 2.6))));
  const vatLabel = uniqueRates.length === 1 ? `TVA ${uniqueRates[0].toFixed(1)}%` : "TVA";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* En-tête */}
        <View style={styles.headerRow}>
          <View style={styles.companyBlock}>
            <Image src={`${window.location.origin}/logo-maisonthai-pdf.png`} style={styles.companyLogo} />
            <View style={styles.companyInfo}>
              <Image
                src={`${typeof window !== "undefined" ? window.location.origin : ""}/company-name-sayonara.png`}
                style={{ width: 179, height: 18, marginBottom: 2 }}
              />
              {(settings?.company_address || "").split("\n").filter(Boolean).map((line, i) => (
                <Text key={i} style={styles.companyMeta}>{line}</Text>
              ))}
              {settings?.vat_number && (
                <Text style={styles.companyMeta}>N° TVA : {settings.vat_number}</Text>
              )}
              {settings?.company_email && (
                <Text style={styles.companyMeta}>{settings.company_email}</Text>
              )}
            </View>
          </View>
          <View style={styles.docBlock}>
            <Text style={styles.docTitle}>BON DE COMMANDE</Text>
            <Text style={styles.docRef}>N° {co.reference || "—"}</Text>
            <Text style={styles.docMeta}>Date commande : {fmtDate(co.order_date)}</Text>
            {co.delivery_date_target && (
              <Text style={styles.docMeta}>Livraison souhaitée : {fmtDate(co.delivery_date_target)}</Text>
            )}
            <Text style={styles.docMeta}>Statut : {STATUS_LABELS[co.status] || co.status}</Text>
          </View>
        </View>

        {/* Parties */}
        <View style={styles.parties}>
          <View style={styles.party}>
            <Text style={styles.partyLabel}>Fournisseur</Text>
            <Text style={styles.partyName}>{COMPANY_HEADER}</Text>
            {(settings?.company_address || "").split("\n").filter(Boolean).map((line, i) => (
              <Text key={i} style={styles.partyText}>{line}</Text>
            ))}
          </View>
          <View style={styles.party}>
            <Text style={styles.partyLabel}>{isWebOrder ? "Client (boutique web)" : "Client"}</Text>
            <Text style={styles.partyName}>{isWebOrder ? (cleanCustomerName || "—") : (co.distributor_name || "—")}</Text>
            {co.distributor_contact ? <Text style={styles.partyText}>{co.distributor_contact}</Text> : null}
            {!isWebOrder && addressLines(co.distributor_address).map((line, i) => (
              <Text key={i} style={styles.partyText}>{line}</Text>
            ))}
          </View>
        </View>

        {/* Phase 87u — Bloc "Adresse de livraison" mis en valeur pour les commandes web.
            Le préparateur n'a plus besoin d'ouvrir l'ERP pour savoir où envoyer le colis. */}
        {isWebOrder && (
          <View style={styles.shipBlock} wrap={false}>
            <Text style={styles.shipKicker}>
              {hasShippingAddress ? "📦 Adresse d'expédition du colis" : isPickup ? "🏪 Retrait sur place" : "Informations livraison"}
            </Text>
            <Text style={styles.shipName}>{cleanCustomerName || "—"}</Text>
            {hasShippingAddress ? (
              shipAddressLines.map((line, i) => (
                <Text key={i} style={styles.shipAddressLine}>{line}</Text>
              ))
            ) : isPickup ? (
              <Text style={styles.shipModePickup}>
                À retirer dans nos locaux — {COMPANY_HEADER}
              </Text>
            ) : (
              <Text style={styles.partyText}>Mode de livraison non précisé</Text>
            )}
            {co.distributor_contact && (
              <Text style={styles.shipMeta}>Contact : {co.distributor_contact}</Text>
            )}
            {desiredDateLabel && (
              <Text style={styles.shipDateBadge}>
                ⏰ Date d&apos;arrivée souhaitée : {desiredDateLabel}
              </Text>
            )}
          </View>
        )}

        {/* Tableau des lignes */}
        <View style={styles.table}>
          <View style={styles.th}>
            <Text style={[styles.thCell, { flex: 2.6 }]}>Désignation</Text>
            <Text style={[styles.thCell, { flex: 0.8 }]}>SKU</Text>
            <Text style={[styles.thCell, { flex: 0.6, textAlign: "right" }]}>Qté</Text>
            <Text style={[styles.thCell, { flex: 0.4 }]}>Unité</Text>
            <Text style={[styles.thCell, { flex: 0.8, textAlign: "right" }]}>PU HT</Text>
            <Text style={[styles.thCell, { flex: 0.8, textAlign: "right" }]}>Total HT</Text>
          </View>
          {lines.map((ln, idx) => {
            const lineHT = (Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0);
            return (
              <View key={idx} style={[styles.tr, idx % 2 === 1 ? styles.trAlt : null]}>
                <View style={{ flex: 2.6 }}>
                  <Text style={styles.td}>{ln.product_name || "—"}</Text>
                  {ln.pack_label && (
                    <Text style={{ fontSize: 7, color: "#A3A6A3", marginTop: 1 }}>{ln.pack_label}</Text>
                  )}
                  {ln.notes && (
                    <Text style={{ fontSize: 7, color: "#A3A6A3", marginTop: 1, fontFamily: "Helvetica-Oblique" }}>
                      {ln.notes}
                    </Text>
                  )}
                </View>
                <Text style={[styles.td, { flex: 0.8 }]}>{ln.product_sku || "—"}</Text>
                <Text style={[styles.td, { flex: 0.6, textAlign: "right" }]}>
                  {Number(ln.quantity || 0).toLocaleString("fr-CH")}
                </Text>
                <Text style={[styles.td, { flex: 0.4 }]}>{ln.unit || "pcs"}</Text>
                <Text style={[styles.td, { flex: 0.8, textAlign: "right" }]}>
                  {ln.unit_price !== null && ln.unit_price !== undefined ? fmtCHF(ln.unit_price) : "—"}
                </Text>
                <Text style={[styles.td, { flex: 0.8, textAlign: "right", fontFamily: "Helvetica-Bold" }]}>
                  {fmtCHF(lineHT)}
                </Text>
              </View>
            );
          })}
        </View>

        {/* Totaux */}
        <View style={styles.totalsBox}>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Sous-total HT</Text>
            <Text style={styles.totalsValue}>{fmtCHF(totalHT)} CHF</Text>
          </View>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>{vatLabel}</Text>
            <Text style={styles.totalsValue}>{fmtCHF(totalVAT)} CHF</Text>
          </View>
          <View style={styles.totalsFinalRow}>
            <Text style={styles.totalsFinalLabel}>Total TTC</Text>
            <Text style={styles.totalsFinalValue}>{fmtCHF(totalTTC)} CHF</Text>
          </View>
        </View>

        {/* Notes */}
        {co.notes && (
          <View style={styles.notes}>
            <Text style={styles.notesLabel}>Notes</Text>
            <Text>{co.notes}</Text>
          </View>
        )}

        {/* Conditions générales */}
        <View style={styles.conditionsBox}>
          <Text style={styles.conditionsTitle}>Conditions</Text>
          <Text style={styles.conditionsText}>
            Document valant bon de commande. Les prix s&apos;entendent en CHF, hors taxes
            sauf indication contraire. TVA suisse applicable au taux réduit 2.6% pour
            les denrées alimentaires. La livraison est confirmée sous réserve de
            disponibilité des stocks et des fabrications. Tout litige doit être signalé
            à réception via {settings?.company_email || "info@maison-thai.pro"}.
          </Text>
        </View>

        {/* Signatures */}
        <View style={styles.signRow}>
          <View style={styles.signBox}>
            <Text style={styles.signLabel}>Émis par</Text>
            <Text style={styles.signValue}>{COMPANY_HEADER}</Text>
            <Text style={styles.signLabel}>{fmtDateTime(co.created_at)}</Text>
          </View>
          <View style={styles.signBox}>
            <Text style={styles.signLabel}>Bon pour accord (client)</Text>
            <Text style={styles.signEmpty}>Nom, date et signature</Text>
          </View>
        </View>

        <Text style={styles.footer}>
          Bon de commande {co.reference} · Maison Thaï · Document confidentiel
        </Text>
      </Page>
    </Document>
  );
};

export default ClientOrderPdfDocument;
